import { Box, Divider, Stack, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { BookingVehicleInfo } from "../FleetManagement/types/FleetManagement-row.types";
import { VehicleInventory } from "../FleetManagement/types/VehicleInventory.types";

export type VehicleInformationCardContentsProps = Readonly<{
  vehicleInfo: BookingVehicleInfo;
  isCalendarDisplay: boolean;
  bookingInventory?: VehicleInventory;
  isCalendarShowInfo?: boolean;
}>;

export type InfoRowProps = {
  label: string;
  value: React.ReactNode;
};

export const InfoRow = ({ label, value }: InfoRowProps) => (
  <Box
    sx={{
      display: "flex",
      justifyContent: "space-between",
      gap: 2
    }}
  >
    <Typography variant="body1" color="text.secondary">
      {label}
    </Typography>
    <Typography variant="body1" fontWeight={500}>
      {value}
    </Typography>
  </Box>
);

/**
 * Reusable Component to display the vehicle information contents
 * in the booking confirm and in the schedule calendar selection
 */
export function VehicleInformationCardContents({
  vehicleInfo,
  isCalendarDisplay,
  isCalendarShowInfo = true
}: VehicleInformationCardContentsProps) {
  const { t } = useTranslation();

  return (
    <Stack spacing={2} data-testid="vehicle-information-card-contents">
      {/* Title */}
      <Box>
        <Typography variant={isCalendarDisplay ? "subtitle1" : "h5"} fontWeight={600}>
          {vehicleInfo.name}
        </Typography>
        <Typography variant="body1" color="text.secondary">
          {vehicleInfo.brandModel}
        </Typography>
      </Box>

      {/* Main details */}
      {isCalendarShowInfo && (
        <>
          <Divider sx={{ mt: 3, mb: 3, opacity: "0.6" }} />

          <Stack spacing={1}>
            <InfoRow label={t("LicensePlate")} value={vehicleInfo.licensePlate} />
            <InfoRow label={t("Vehicle Type")} value={vehicleInfo.vehicleType ?? "-"} />
            <InfoRow label={t("Fuel Type")} value={t(vehicleInfo.fuel)} />
            <InfoRow label={t("Reach")} value={`${vehicleInfo.reach} km`} />
            <InfoRow label={t("Number of Seats")} value={vehicleInfo.numSeats} />
            {vehicleInfo.pricePerHour != null && (
              <InfoRow label={t("Price (h)")} value={vehicleInfo.pricePerHour} />
            )}
          </Stack>

          {vehicleInfo.properties && vehicleInfo.properties.length > 0 && (
            <>
              <Divider sx={{ mt: 3, mb: 3, opacity: "0.6" }} />

              <Box>
                <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                  {t("Properties")}
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  {vehicleInfo.properties.map(p => p.name).join(", ")}
                </Typography>
              </Box>
            </>
          )}

          {vehicleInfo.description && (
            <>
              <Divider sx={{ mt: 3, mb: 3, opacity: "0.6" }} />
              <Box>
                <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                  {t("Description")}
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  {vehicleInfo.description}
                </Typography>
              </Box>
            </>
          )}

          {vehicleInfo.userNotes && (
            <>
              <Divider sx={{ mt: 3, mb: 3, opacity: "0.6" }} />
              <Box>
                <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                  {t("Notes")}
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  {vehicleInfo.userNotes}
                </Typography>
              </Box>
            </>
          )}
        </>
      )}
    </Stack>
  );
}
